"use client";

import React from 'react';
import Link from 'next/link';
import {motion, AnimatePresence} from 'framer-motion';
import {useGame} from '@/context/GameContext';
import {useTheme} from '@/context/ThemeContext';
import Lobby from '@/components/Lobby';
import GameBoard from '@/components/GameBoard';
import {useGameRules} from '@/hooks/useGameRules';
import {RulesModal} from '@/components/RulesModal';
import {RulesTriggerIcon} from '@/components/RulesTriggerIcon';
import {FeedbackModal, FeedbackType} from '@/components/FeedbackModal';
import {FloatingSuggestButton} from '@/components/FloatingSuggestButton';
import {GameRatingModal} from '@/components/GameRatingModal';
import {GameDemoOverlay} from '@/components/GameDemoOverlay';
import {
  Loader2,
  AlertCircle,
  Copy,
  Check,
  X,
  LogOut,
  Users,
  User,
  Sparkles,
  Share2,
  Play,
  Dices, Swords, Globe, WifiOff, Settings, Coffee, Sun, Moon, Info, Gamepad2, BookOpen, Star
} from 'lucide-react';
import {clsx, type ClassValue} from 'clsx';
import {twMerge} from 'tailwind-merge';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

function ConnectingView() {
  const [msgIndex, setMsgIndex] = React.useState(0);
  const [splats, setSplats] = React.useState<{
    id: number,
    word: string,
    x: number,
    y: number,
    rotate: number,
    color: string
  }[]>([]);

  const messages = [
    "Duke u lidhur me botën...",
    "Duke mprehur lapsat...",
    "Duke hapur fjalorin...",
    "Duke përgatitur sfidat...",
    "Duke pastruar tabelën..."
  ];

  const SPLAT_WORDS = ["FJALA", "FUNDIT", "LOJA", "SHQIP", "MENDOHU", "GATI", "SFIDA", "GJENIU", "ALFABETI", "KUPTIMI"];
  const COLORS = ["#F43F5E", "#0EA5E9", "#F59E0B", "#10B981", "#8B5CF6"];

  React.useEffect(() => {
    const timer = setInterval(() => {
      setMsgIndex((prev) => (prev + 1) % messages.length);
    }, 2000);

    const splatInterval = setInterval(() => {
      const randomWord = SPLAT_WORDS[Math.floor(Math.random() * SPLAT_WORDS.length)];
      const randomX = 10 + Math.random() * 80;
      const randomY = 10 + Math.random() * 80;
      const randomRotate = Math.random() * 40 - 20;
      const randomColor = COLORS[Math.floor(Math.random() * COLORS.length)];

      const newSplat = {
        id: Date.now(),
        word: randomWord,
        x: randomX,
        y: randomY,
        rotate: randomRotate,
        color: randomColor
      };
      setSplats(prev => [...prev.slice(-15), newSplat]);
    }, 800);

    return () => {
      clearInterval(timer);
      clearInterval(splatInterval);
    };
  }, [messages.length]);

  return (
      <div
          className="h-[100dvh] bg-background flex flex-col items-center justify-center p-4 text-center overflow-hidden relative">
        {/* Background Word Splats */}
        <AnimatePresence>
          {splats.map((splat) => (
              <motion.div
                  key={splat.id}
                  initial={{scale: 0, opacity: 0, rotate: splat.rotate - 20}}
                  animate={{scale: 1, opacity: 0.1, rotate: splat.rotate}}
                  exit={{scale: 1.5, opacity: 0}}
                  transition={{type: "spring", damping: 12, stiffness: 200}}
                  className="absolute font-black handwritten pointer-events-none select-none text-2xl md:text-6xl"
                  style={{
                    left: `${splat.x}%`,
                    top: `${splat.y}%`,
                    color: splat.color
                  }}
              >
                {splat.word}
              </motion.div>
          ))}
        </AnimatePresence>

        {/* Background Scribbles */}
        <div className="absolute inset-0 pointer-events-none opacity-5">
          <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <motion.path
                d="M10,10 Q30,50 10,90 M90,10 Q70,50 90,90"
                stroke="currentColor"
                strokeWidth="0.5"
                fill="none"
                animate={{pathLength: [0, 1]}}
                transition={{duration: 2, repeat: Infinity, repeatType: "reverse"}}
            />
          </svg>
        </div>

        <div className="relative space-y-6 md:space-y-8">
          <motion.div
              animate={{
                y: [0, -10, 0],
                rotate: [0, 5, -5, 0]
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="relative inline-block"
          >
            <div
                className="w-24 h-24 md:w-32 md:h-32 bg-pink-100 rounded-full doodle-border flex items-center justify-center shadow-doodle">
              <motion.div
                  animate={{rotate: 360}}
                  transition={{duration: 10, repeat: Infinity, ease: "linear"}}
              >
                <Globe className="w-12 h-12 md:w-16 md:h-16 text-pink-500 stroke-[2.5px]"/>
              </motion.div>
            </div>

            <motion.div
                animate={{scale: [1, 1.2, 1], opacity: [0.5, 1, 0.5]}}
                transition={{duration: 2, repeat: Infinity}}
                className="absolute -top-3 -right-3 md:-top-4 md:-right-4 text-amber-400"
            >
            </motion.div>
          </motion.div>

          <div className="space-y-2 md:space-y-3">
            <motion.div
                key={msgIndex}
                animate={{opacity: 1, y: 0}}
                className="text-2xl md:text-3xl font-black text-foreground handwritten uppercase tracking-tight"
            >
              {messages[msgIndex]}
            </motion.div>
            <div className="flex items-center justify-center gap-1.5 md:gap-2">
              {[0, 1, 2].map((i) => (
                  <motion.div
                      key={i}
                      animate={{scale: [1, 1.5, 1]}}
                      transition={{duration: 0.6, repeat: Infinity, delay: i * 0.2}}
                      className="w-2.5 h-2.5 md:w-3 md:h-3 bg-sky-400 rounded-full doodle-border-soft"
                  />
              ))}
            </div>
          </div>
        </div>
      </div>
  );
}

function WaitingScreen() {
  const {gameState, resetGame, cancelPrivateGame, playerName} = useGame();
  const [copied, setCopied] = React.useState(false);
  const [linkCopied, setLinkCopied] = React.useState(false);
  const [factIndex, setFactIndex] = React.useState(0);

  const FACTS = [
    "Shqipja është një nga gjuhët më të vjetra dhe unike në Evropë.",
    "Alfabeti i gjuhës shqipe përbëhet nga 36 shkronja: 7 zanore dhe 29 bashkëtingëllore.",
    "Shqipja përdor 9 dyshkronjësha (digrafe) për të paraqitur tinguj specifikë: dh, gj, ll, nj, rr, sh, th, xh, zh.",
    "Shkronja \"Ë\" është zanorja më e përdorur në strukturën e fjalëve të gjuhës shqipe.",
    "Libri i parë i botuar në gjuhën shqipe është \"Meshari\" i Gjon Buzukut, i cili daton në vitin 1555.",
    "Dokumenti i parë i shkruar që njihet në gjuhën shqipe është \"Formula e Pagëzimit\" e vitit 1462.",
    "Alfabeti modern i shqipes u unifikua në Kongresin e Manastirit në vitin 1908, duke përdorur shkronja latine.",
    "Gjuha shqipe flitet nga rreth 7 deri në 10 milionë njerëz në mbarë botën.",
    "Gjuha shqipe përdor një sistem prej pesë rasash gramatikore: emërore, gjinore, dhanore, kallëzore dhe rrjedhore.",
    "Gjuha shqipe ka dy dialekte kryesore: Gegë (në veri) dhe Toskë (në jug).",
    "Lumi Shkumbin shërben historikisht si kufiri natyror midis dy dialekteve kryesore: Gegërishtes dhe Toskërishtes.",
    "Gjuha Standarde Shqipe e ditëve të sotme bazohet kryesisht në dialektin toskë, por përfshin elemente të rëndësishme nga gegërishtja.",
    "Megjithëse ka pasur kontakte me gjuhë të tjera, fjalori bazë i shqipes ruan një origjinalitet të plotë autokton.",
    "Shqipja vlerësohet si pasardhëse e drejtpërdrejtë e gjuhës ilire.",
    "Gjuha shqipe ka një sistem foljor shumë të pasur që përfshin 8 mënyra të ndryshme.",
    "Shqipja është gjuhë zyrtare në Shqipëri, Kosovë dhe Maqedoninë e Veriut.",
    "Një veçori unike e shqipes është \"Mënyra Habitore\", e cila përdoret posaçërisht për të shprehur çudi ose habitje (p.sh. paska!).",
    "Shqipja ka trajta të shquara dhe të pashquara që integrohen drejtpërdrejt në formën e emrit (p.sh., qytet dhe qyteti).",
    "Gjuha shqipe ka një sistem shumë të saktë fonetik: çdo shkronjë i përgjigjet vetëm një tingulli.",
    "Shqipja qëndron si një degë e vetme dhe unike në pemën e gjuhëve indoevropiane; kjo është pasuria jonë e papërsëritshme që duhet ta mbajmë gjallë gjithmonë."
  ];

  React.useEffect(() => {
    const interval = setInterval(() => {
      setFactIndex((prev) => (prev + 1) % FACTS.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [FACTS.length]);

  const isCustom = gameState?.mode === 'PVP_PRIVATE_ROOM';
  const gameCode = gameState?.gameId ? gameState.gameId.substring(0, 6).toUpperCase() : '......';

  const getJoinLink = () => {
    return `${window.location.origin}?join=${gameState?.gameId}`;
  };

  const copyToClipboard = async () => {
    if (gameState?.gameId) {
      const text = gameState.gameId;
      let success = false;

      if (navigator.clipboard && window.isSecureContext) {
        try {
          await navigator.clipboard.writeText(text);
          success = true;
        } catch (err) {
          console.error('Clipboard API failed', err);
        }
      }

      if (!success) {
        // Fallback for non-secure contexts or unsupported browsers
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.left = "-9999px";
        textArea.style.top = "0";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
          document.execCommand('copy');
          success = true;
        } catch (err) {
          console.error('Fallback copy failed', err);
        }
        document.body.removeChild(textArea);
      }

      if (success) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  };

  const shareGame = async () => {
    const link = getJoinLink();
    let success = false;

    // Attempt to copy to clipboard first
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(link);
        success = true;
      } catch (err) {
        console.error('Clipboard API failed', err);
      }
    }

    if (!success) {
      const textArea = document.createElement("textarea");
      textArea.value = link;
      textArea.style.position = "fixed";
      textArea.style.left = "-9999px";
      textArea.style.top = "0";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        success = true;
      } catch (err) {
        console.error('Fallback copy failed', err);
      }
      document.body.removeChild(textArea);
    }

    if (success) {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }

    // Only attempt native share on mobile devices where it's more useful
    if (navigator.share && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) && gameState?.gameId) {
      try {
        await navigator.share({
          title: 'Luaj FJALAFUNDIT me mua!',
          text: `Eja luajmë FJALAFUNDIT! Kodi i lojës: ${gameCode}`,
          url: link,
        });
      } catch (err) {
        if (process.env.NODE_ENV === 'development') console.log('Error sharing', err);
      }
    }
  };

  const lobbyParticles = React.useMemo(() => [...Array(12)].map((_, i) => ({
    id: i,
    duration: 4 + Math.random() * 4,
    delay: Math.random() * 5,
    top: 10 + Math.random() * 80,
    left: 10 + Math.random() * 80,
    size: 16 + Math.random() * 16
  })), []);

  return (
      <div
          className="h-[100dvh] bg-background flex flex-col items-center justify-center p-4 text-center overflow-hidden relative">
        {/* Decorative background doodles */}
        <div className="absolute inset-0 pointer-events-none opacity-10">
          <motion.div
              animate={{scale: [1, 1.1, 1], rotate: [0, 10, -10, 0]}}
              transition={{duration: 10, repeat: Infinity}}
              className="absolute top-1/4 left-10 text-pink-400"
          >
            <Sparkles size={120}/>
          </motion.div>
          <motion.div
              animate={{scale: [1.1, 1, 1.1], rotate: [0, -10, 10, 0]}}
              transition={{duration: 8, repeat: Infinity}}
              className="absolute bottom-1/4 right-10 text-sky-400"
          >
            <Swords size={100}/>
          </motion.div>
        </div>

        <motion.div
            initial={{opacity: 0, scale: 0.9}}
            animate={{opacity: 1, scale: 1}}
            className="w-full max-w-xl relative z-10"
        >
          <div className="space-y-2 md:space-y-10">
            <div
                className="bg-card doodle-border p-4 md:p-12 shadow-doodle-xl relative overflow-hidden">
              {/*<div className="absolute top-0 right-0 p-4 opacity-20">*/}
              {/*  <Sparkles className="w-8 h-8 md:w-10 md:h-10 text-amber-400"/>*/}
              {/*</div>*/}

              {isCustom ? (
                  <div className="space-y-3 md:space-y-8">
                    <div className="space-y-1 md:space-y-4">
                      <motion.div
                          animate={{rotate: [-5, 5, -5]}}
                          transition={{duration: 3, repeat: Infinity}}
                          className="w-12 h-12 md:w-20 md:h-20 bg-amber-100 doodle-border-soft flex items-center justify-center mx-auto shadow-doodle-sm"
                      >
                        <Users className="w-6 h-6 md:w-10 md:h-10 text-amber-500 stroke-[3px]"/>
                      </motion.div>
                      <h2 className="text-2xl md:text-4xl font-black text-foreground handwritten uppercase tracking-tight">LOJË
                        PRIVATE</h2>
                      <p className="font-bold handwritten text-slate-500 max-w-[280px] mx-auto leading-tight italic text-[10px] md:text-base">
                        Dërgojani këtë kod shokut/shoqes për t'u bashkuar në lojë:
                      </p>
                    </div>

                    <div
                        className="relative group bg-muted doodle-border-soft p-3 md:p-8 flex flex-col items-center gap-3 md:gap-6 shadow-inner rotate-[-1deg]">
                      <div className="flex gap-1 md:gap-2">
                        {gameCode.split('').map((char, i) => (
                            <motion.span
                                key={i}
                                initial={{y: 10, opacity: 0}}
                                animate={{y: 0, opacity: 1}}
                                transition={{delay: i * 0.05}}
                                className="text-xl md:text-5xl font-black text-foreground handwritten w-8 h-10 md:w-10 md:h-14 bg-card flex items-center justify-center rounded-lg shadow-sm border-2 border-slate-100"
                            >
                              {char}
                            </motion.span>
                        ))}
                      </div>

                      <div className="flex gap-2 md:gap-3 w-full">
                        <button
                            onClick={copyToClipboard}
                            className={cn(
                                "flex-1 flex items-center justify-center gap-1 md:gap-2 py-2.5 md:py-4 doodle-border-soft font-black handwritten transition-all active:scale-95 shadow-doodle-sm text-xs md:text-base",
                                copied ? "bg-emerald-500 text-white border-emerald-600" : "bg-amber-400 text-white hover:bg-amber-500"
                            )}
                        >
                          {copied ? <Check className="w-3.5 h-3.5 md:w-5 md:h-5"/> :
                              <Copy className="w-3.5 h-3.5 md:w-5 md:h-5"/>}
                          {copied ? 'U KOPJUA!' : 'KOPJO'}
                        </button>

                        <button
                            onClick={shareGame}
                            className={cn(
                                "p-2.5 md:p-4 doodle-border-soft font-black shadow-doodle-sm transition-all active:scale-95 flex items-center justify-center gap-2",
                                linkCopied ? "bg-emerald-500 text-white border-emerald-600" : "bg-sky-400 text-white hover:bg-sky-500"
                            )}
                        >
                          {linkCopied ? <Check className="w-5 h-5 md:w-6 md:h-6"/> :
                              <Share2 className="w-5 h-5 md:w-6 md:h-6"/>}
                        </button>
                      </div>
                    </div>

                    <div
                        className="flex items-center justify-center gap-2 bg-sky-50 py-1.5 md:py-3 px-4 md:px-6 doodle-border-soft border-sky-100">
                      <Loader2 className="w-3.5 h-3.5 md:w-5 md:h-5 text-sky-500 animate-spin"/>
                      <span
                          className="font-black handwritten text-sky-600 animate-pulse text-[10px] md:text-sm uppercase tracking-wider">Duke pritur...</span>
                    </div>
                  </div>
              ) : (
                  <div className="space-y-3 md:space-y-8">
                    <div className="relative">
                      <motion.div
                          animate={{
                            scale: [1, 1.1, 1],
                            rotate: [0, 8, -8, 0],
                            y: [0, -10, 0]
                          }}
                          transition={{duration: 4, repeat: Infinity, ease: "easeInOut"}}
                          className="w-16 h-16 md:w-24 md:h-24 bg-pink-100 doodle-border flex items-center justify-center mx-auto shadow-doodle mb-1"
                      >
                        <Swords className="w-8 h-8 md:w-12 md:h-12 text-pink-500 stroke-[2.5px]"/>
                      </motion.div>
                      <motion.div
                          animate={{opacity: [0, 1, 0], scale: [0.8, 1.2, 0.8], rotate: 360}}
                          transition={{duration: 3, repeat: Infinity}}
                          className="absolute -top-3 -right-3 md:-top-4 md:-right-4 text-amber-400"
                      >
                        <Gamepad2 className="w-8 h-8 md:w-10 md:h-10"/>
                      </motion.div>
                    </div>

                    <div className="space-y-1 md:space-y-3">
                      <h2 className="text-xl md:text-5xl font-black text-foreground handwritten uppercase tracking-tight leading-none">
                        DUKE KËRKUAR...
                      </h2>
                      <p className="font-bold handwritten text-slate-500 max-w-[280px] mx-auto leading-tight italic text-[10px] md:text-lg">
                        Po kërkojmë një kundërshtar!
                      </p>
                    </div>

                    <div
                        className="bg-sky-50 p-3 md:p-8 doodle-border-soft border-2 border-sky-100 relative min-h-[80px] md:min-h-[160px] flex flex-col justify-center shadow-inner rotate-[1deg]">
                      <div
                          className="absolute -top-2 md:-top-3 left-4 md:left-6 bg-sky-400 text-white px-2 md:px-4 py-0.5 md:py-1 doodle-border-soft text-[8px] md:text-xs font-black uppercase tracking-widest handwritten shadow-sm rotate-[-2deg]">
                        A E DINIT?
                      </div>
                      <AnimatePresence mode="wait">
                        <motion.p
                            key={factIndex}
                            initial={{opacity: 0, y: 10}}
                            animate={{opacity: 1, y: 0}}
                            exit={{opacity: 0, y: -10}}
                            className="text-sky-800 font-black handwritten text-sm md:text-xl leading-snug"
                        >
                          &quot;{FACTS[factIndex]}&quot;
                        </motion.p>
                      </AnimatePresence>
                    </div>
                  </div>
              )}
            </div>

            <motion.button
                whileHover={{scale: 1.02}}
                whileTap={{scale: 0.98}}
                onClick={isCustom ? cancelPrivateGame : resetGame}
                className="w-full bg-card doodle-border p-3 md:p-6 flex items-center justify-center group hover:bg-rose-50 transition-all shadow-doodle hover:shadow-doodle-sm hover:translate-x-[2px] hover:translate-y-[2px]"
            >
            <span
                className="flex items-center gap-2 md:gap-3 font-black text-slate-500 group-hover:text-rose-500 handwritten text-sm md:text-2xl transition-colors tracking-widest uppercase">
              ANULO LOJËN
            </span>
            </motion.button>
          </div>
        </motion.div>

        {/* Floating particles - spread out */}
        {lobbyParticles.map((p) => (
            <motion.div
                key={p.id}
                animate={{
                  opacity: [0, 0.3, 0],
                  scale: [0.5, 1, 0.5],
                  y: [-40, 40],
                  x: [-20, 20]
                }}
                transition={{
                  duration: p.duration,
                  repeat: Infinity,
                  delay: p.delay
                }}
                className="absolute text-amber-300 pointer-events-none"
                style={{
                  top: `${p.top}%`,
                  left: `${p.left}%`
                }}
            >
              <Gamepad2 size={p.size}/>
            </motion.div>
        ))}
      </div>
  );
}

function OpponentLeftView() {
  const particles = React.useMemo(() => [...Array(6)].map((_, i) => ({
    id: i,
    duration: 3 + Math.random() * 2,
    delay: Math.random() * 2,
    top: Math.random() * 100,
    left: Math.random() * 100
  })), []);

  return (
      <div
          className="h-[100dvh] bg-background flex flex-col items-center justify-center p-4 text-center overflow-hidden relative">
        {/* Decorative background doodles */}
        <div className="absolute inset-0 pointer-events-none opacity-10">
          <motion.div
              animate={{
                scale: [1, 1.1, 1],
                rotate: [0, 10, -10, 0]
              }}
              transition={{duration: 10, repeat: Infinity}}
              className="absolute top-1/4 left-10 text-rose-400"
          >
            <LogOut size={120}/>
          </motion.div>
          <motion.div
              animate={{
                scale: [1.1, 1, 1.1],
                rotate: [0, -10, 10, 0]
              }}
              transition={{duration: 8, repeat: Infinity}}
              className="absolute bottom-1/4 right-10 text-amber-400"
          >
            <Sparkles size={100}/>
          </motion.div>
        </div>

        <motion.div
            initial={{opacity: 0, scale: 0.8}}
            animate={{opacity: 1, scale: 1}}
            className="w-full max-w-2xl relative z-10"
        >
          <div className="space-y-4 md:space-y-12">
            {/* Main Title with organic movement */}
            <motion.div
                animate={{y: [0, -5, 0]}}
                transition={{duration: 2, repeat: Infinity, ease: "easeInOut"}}
                className="space-y-1.5 md:space-y-4"
            >
              <div
                  className="inline-block bg-rose-500 text-white px-3 py-1 md:px-6 md:py-2 doodle-border rotate-[-2deg] font-black text-sm md:text-xl handwritten shadow-doodle-sm">
                KUNDËRSHTARI DOLI!
              </div>
              <h2 className="text-3xl md:text-7xl font-black text-foreground handwritten uppercase drop-shadow-sm leading-tight">
                PO KTHEHEMI NË <span className="text-rose-500">LOB</span>...
              </h2>
            </motion.div>

            <div className="flex flex-col items-center gap-4 md:gap-8">
              <div className="bg-card doodle-border p-4 md:p-10 shadow-doodle-lg rotate-1 max-w-md">
                <p className="text-foreground font-black handwritten text-lg md:text-3xl leading-tight italic">
                  "Sfida u ndërpre, por loja nuk mbaron këtu!"
                </p>
              </div>

              <div className="flex justify-center">
                <div
                    className="w-10 h-10 md:w-16 md:h-16 border-4 md:border-8 border-rose-500 border-t-transparent rounded-full animate-spin"/>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
  );
}

function SettingsModal({isOpen, onClose}: { isOpen: boolean, onClose: () => void }) {
  const {
    breakReminderEnabled,
    setBreakReminderEnabled,
    customKeyboardEnabled,
    setCustomKeyboardEnabled,
    setIsRatingModalVisible
  } = useGame();
  const {theme, toggleTheme} = useTheme();
  const [isDictionaryInfoOpen, setIsDictionaryInfoOpen] = React.useState(false);

  return (
      <>
        <DictionaryInfoModal isOpen={isDictionaryInfoOpen} onClose={() => setIsDictionaryInfoOpen(false)} />
        <AnimatePresence>
          {isOpen && (
              <div
                  className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm">
                <motion.div
                    initial={{opacity: 0, scale: 0.9, y: 20}}
                    animate={{opacity: 1, scale: 1, y: 0}}
                    exit={{opacity: 0, scale: 0.9, y: 20}}
                    className="relative w-full max-w-sm bg-card doodle-border p-5 md:p-8 shadow-doodle-xl overflow-y-auto max-h-[90dvh]"
                >
                  <button
                      onClick={onClose}
                      className="absolute top-4 right-4 p-2 hover:bg-muted rounded-full transition-colors boing z-10"
                  >
                    <X className="w-6 h-6 text-slate-400"/>
                  </button>

                  <div className="space-y-6">
                    <div className="text-center space-y-1">
                      <div
                          className="inline-flex items-center gap-2 bg-muted text-slate-600 px-4 py-1 rounded-full text-xs font-black uppercase tracking-widest handwritten">
                        <Settings className="w-4 h-4"/>
                        OPSIONET
                      </div>
                      <h2 className="text-2xl font-black text-foreground handwritten uppercase">CILËSIMET</h2>
                    </div>

                    <div className="space-y-4">
                      {/* Theme Switcher */}
                      <div
                          className="flex items-center justify-between p-3 bg-muted/50 doodle-border-soft border-muted">
                        <div className="space-y-0.5">
                          <div className="font-black text-foreground handwritten text-base leading-tight">Tema e errët</div>
                          <p className="text-slate-500 text-[10px] font-bold uppercase tracking-wide">{theme === 'dark' ? 'Aktiv' : 'Joaktiv'}</p>
                        </div>
                        <button
                            onClick={toggleTheme}
                            className={cn(
                                "w-12 h-7 rounded-full p-1 transition-colors duration-300 flex items-center",
                                theme === 'dark' ? "bg-indigo-500" : "bg-slate-300"
                            )}
                        >
                          <motion.div
                              animate={{x: theme === 'dark' ? 20 : 0}}
                              transition={{type: "spring", stiffness: 500, damping: 30}}
                              className="w-5 h-5 bg-card rounded-full shadow-sm flex items-center justify-center"
                          >
                            {theme === 'dark' ? <Moon className="w-3 h-3 text-indigo-600"/> :
                                <Sun className="w-3 h-3 text-amber-500"/>}
                          </motion.div>
                        </button>
                      </div>

                      <div
                          className="flex items-center justify-between p-3 bg-muted/50 doodle-border-soft border-muted">
                        <div className="space-y-0.5">
                          <div
                              className="font-black text-foreground handwritten text-base leading-tight">Përkujtuesi i pushimit</div>
                          <p className="text-slate-400 text-[10px] font-bold uppercase tracking-wide">Pas 1 ore lojë</p>
                        </div>
                        <button
                            onClick={() => setBreakReminderEnabled(!breakReminderEnabled)}
                            className={cn(
                                "w-12 h-7 rounded-full p-1 transition-colors duration-300",
                                breakReminderEnabled ? "bg-emerald-400" : "bg-slate-300"
                            )}
                        >
                          <motion.div
                              animate={{x: breakReminderEnabled ? 20 : 0}}
                              transition={{type: "spring", stiffness: 500, damping: 30}}
                              className="w-5 h-5 bg-card rounded-full shadow-sm"
                          />
                        </button>
                      </div>

                      <div
                          className="flex items-center justify-between p-3 bg-muted/50 doodle-border-soft border-muted">
                        <div className="space-y-0.5">
                          <div
                              className="font-black text-foreground handwritten text-base leading-tight">Tastiera e lojës</div>
                          <p className="text-slate-400 text-[10px] font-bold uppercase tracking-wide">Përdor tastierën vizuale</p>
                        </div>
                        <button
                            onClick={() => setCustomKeyboardEnabled(!customKeyboardEnabled)}
                            className={cn(
                                "w-12 h-7 rounded-full p-1 transition-colors duration-300",
                                customKeyboardEnabled ? "bg-amber-400" : "bg-slate-300"
                            )}
                        >
                          <motion.div
                              animate={{x: customKeyboardEnabled ? 20 : 0}}
                              transition={{type: "spring", stiffness: 500, damping: 30}}
                              className="w-5 h-5 bg-card rounded-full shadow-sm"
                          />
                        </button>
                      </div>

                      {/* How to Play Link */}
                      <Link 
                        href="/si-te-luajme/"
                        className="w-full flex items-center justify-between p-3 bg-pink-500/10 doodle-border-soft border-pink-400/30 group hover:bg-pink-500/20 transition-all"
                      >
                        <div className="text-left space-y-0.5">
                          <div className="font-black text-foreground handwritten text-base leading-tight">Udhëzuesi i lojës</div>
                          <p className="text-pink-500 text-[10px] font-bold uppercase tracking-wide">Si të luajmë?</p>
                        </div>
                        <BookOpen className="w-5 h-5 text-pink-500 group-hover:scale-110 transition-transform" />
                      </Link>

                      {/* Dictionary Info */}
                      <button 
                        onClick={() => setIsDictionaryInfoOpen(true)}
                        className="w-full flex items-center justify-between p-3 bg-sky-500/10 doodle-border-soft border-sky-400/30 group hover:bg-sky-500/20 transition-all"
                      >
                        <div className="text-left space-y-0.5">
                          <div className="font-black text-foreground handwritten text-base leading-tight">Rreth fjalorit</div>
                          <p className="text-sky-500 text-[10px] font-bold uppercase tracking-wide">Burimet dhe saktësia</p>
                        </div>
                        <Info className="w-5 h-5 text-sky-500 group-hover:scale-110 transition-transform" />
                      </button>

                      {/* Game Rating Manual Trigger */}
                      <button 
                        onClick={() => {
                          onClose();
                          setIsRatingModalVisible(true);
                        }}
                        className="w-full flex items-center justify-between p-3 bg-amber-500/10 doodle-border-soft border-amber-400/30 group hover:bg-amber-500/20 transition-all"
                      >
                        <div className="text-left space-y-0.5">
                          <div className="font-black text-foreground handwritten text-base leading-tight">Vlerëso lojën</div>
                          <p className="text-amber-500 text-[10px] font-bold uppercase tracking-wide">Na tregoni mendimin tuaj</p>
                        </div>
                        <Star className="w-5 h-5 text-amber-500 group-hover:scale-110 transition-transform fill-current" />
                      </button>
                    </div>

                    <button
                        onClick={onClose}
                        className="w-full bg-pink-500 text-white py-3 doodle-border shadow-doodle-lg font-black text-lg hover:translate-x-[-2px] hover:translate-y-[-2px] transition-all active:scale-95"
                    >
                      MBYLL
                    </button>
                  </div>
                </motion.div>
              </div>
          )}
        </AnimatePresence>
      </>
  );
}

function BreakReminderModal() {
  const {isBreakReminderVisible, dismissBreakReminder} = useGame();

  return (
      <AnimatePresence>
        {isBreakReminderVisible && (
            <div
                className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-md">
              <motion.div
                  initial={{opacity: 0, scale: 0.8, rotate: -5}}
                  animate={{opacity: 1, scale: 1, rotate: 0}}
                  exit={{opacity: 0, scale: 0.8, rotate: 5}}
                  className="relative w-full max-w-sm bg-card doodle-border p-8 md:p-10 shadow-doodle-xl text-center space-y-6"
              >
                <div
                    className="w-20 h-20 bg-amber-100 doodle-border-soft flex items-center justify-center mx-auto -rotate-6 shadow-doodle-sm !shadow-[#F59E0B]">
                  <Coffee className="w-10 h-10 text-amber-500 stroke-[3px]"/>
                </div>

                <div className="space-y-2">
                  <h2 className="text-3xl font-black text-foreground handwritten uppercase leading-tight">KOHA
                    PËR PUSHIM!</h2>
                  <p className="font-bold handwritten text-slate-500 leading-snug">
                    Keni më shumë se një orë që luani! Një pushim i shkurtër do t&apos;ju ndihmonte.
                  </p>
                </div>

                <div className="pt-2">
                  <button
                      onClick={dismissBreakReminder}
                      className="w-full bg-emerald-500 text-white py-4 doodle-border shadow-doodle-lg font-black text-xl hover:translate-x-[-2px] hover:translate-y-[-2px] transition-all active:scale-95 boing"
                  >
                    KUPTOVA, FALEMINDERIT!
                  </button>
                </div>
              </motion.div>
            </div>
        )}
      </AnimatePresence>
  );
}

export default function FjalaEFunditPage() {
  const {
    status,
    isConnected,
    isSystemHealthy,
    triggerReady,
    resetGame,
    gameCount,
    isRatingModalVisible,
    setIsRatingModalVisible,
    isOpponentDisconnected
  } = useGame();
    const {isRulesOpen, hasSeenRules, openRules, closeRules} = useGameRules();
    const [isSettingsOpen, setIsSettingsOpen] = React.useState(false);
    const [isRatingAutoPopped, setIsRatingAutoPopped] = React.useState(false);
    const [isDemoOpen, setIsDemoOpen] = React.useState(false);
    const [hasDismissedRating, setHasDismissedRating] = React.useState(false);

    const [isFeedbackModalOpen, setIsFeedbackModalOpen] = React.useState(false);
    const [feedbackCategory, setFeedbackCategory] = React.useState<FeedbackType>('SUGGESTION');

    React.useEffect(() => {
      const handleOpenDemo = () => setIsDemoOpen(true);
      window.addEventListener('open-demo-overlay', handleOpenDemo);
      return () => window.removeEventListener('open-demo-overlay', handleOpenDemo);
    }, []);

    const openFeedback = (type: FeedbackType) => {
      setFeedbackCategory(type);
      setIsFeedbackModalOpen(true);
    };

    const ratingRandomThreshold = React.useMemo(() => Math.random(), []);

    // Smart Rating Logic
    React.useEffect(() => {
      if (status === 'LOBBY' && gameCount >= 10 && !hasDismissedRating) {
        const lastRatingDate = localStorage.getItem('fjalafundit_last_rating_date');
        const now = new Date();

        let shouldShow = false;
        if (!lastRatingDate) {
          shouldShow = true;
        } else {
          const lastDate = new Date(lastRatingDate);
          const diffDays = Math.ceil(Math.abs(now.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24));
          if (diffDays >= 30) {
            shouldShow = true;
          }
        }

        if (shouldShow && !isRatingModalVisible) {
          // 20% random chance to show when in lobby
          if (ratingRandomThreshold < 0.2) {
            setIsRatingAutoPopped(true);
            setIsRatingModalVisible(true);
          }
        }
      }
    }, [status, gameCount, setIsRatingModalVisible, isRatingModalVisible, ratingRandomThreshold, hasDismissedRating]);

  return (
      <>
        <div data-nosnippet>
          <BreakReminderModal/>
          <SettingsModal isOpen={isSettingsOpen} onClose={() => setIsSettingsOpen(false)}/>
        </div>

        {isSystemHealthy === false ? (
            <div data-nosnippet className="h-[100dvh] bg-background flex flex-col items-center justify-center p-4">
              <div
                  className="w-full max-w-md bg-card doodle-border p-6 md:p-12 text-center shadow-doodle-xl">
                <div
                    className="w-16 h-16 md:w-24 md:h-24 bg-rose-100 doodle-border-soft flex items-center justify-center mb-4 md:mb-8 mx-auto -rotate-6 shadow-doodle-sm !shadow-[#F43F5E]">
                  <AlertCircle className="w-8 h-8 md:w-12 md:h-12 text-rose-500 stroke-[3px]"/>
                </div>
                <h2 className="text-2xl md:text-4xl font-black text-foreground mb-4 md:mb-6 handwritten uppercase">OHO!</h2>
                <div
                    className="bg-rose-50 p-4 md:p-6 doodle-border-soft border-rose-200 mb-6 md:mb-8 rotate-1">
                  <p className="text-rose-600 font-bold handwritten text-base md:text-xl leading-relaxed">
                    Loja ka probleme teknike, ejani përsëri më vonë.
                  </p>
                </div>
                <button
                    onClick={() => window.location.reload()}
                    className="w-full bg-emerald-500 text-white py-4 md:py-5 doodle-border shadow-doodle md:shadow-doodle-lg font-black text-lg md:text-xl hover:translate-x-[-2px] hover:translate-y-[-2px] transition-all active:scale-95"
                >
                  PROVO PËRSËRI
                </button>
              </div>
            </div>
        ) : (isSystemHealthy === null || !isConnected) ? (
            <div data-nosnippet>
              <ConnectingView/>
            </div>
        ) : status === 'LOBBY' ? (
            <div className="h-[100dvh] bg-background flex items-center justify-center p-4">
              <Lobby/>
            </div>
        ) : status === 'PREPARING' ? (
            <div data-nosnippet
                className="h-[100dvh] bg-background flex flex-col items-center justify-center p-4 text-center">
              <motion.div
                  initial={{opacity: 0, scale: 0.9}}
                  animate={{opacity: 1, scale: 1}}
                  className="w-full max-w-md bg-card doodle-border p-6 md:p-12 shadow-doodle-xl"
              >
                <div
                    className="w-16 h-16 md:w-20 md:h-20 bg-amber-100 doodle-border-soft flex items-center justify-center mb-4 md:mb-6 mx-auto -rotate-6 shadow-doodle-sm !shadow-[#F59E0B]">
                  <Dices className="w-8 h-8 md:w-10 md:h-10 text-amber-500 stroke-[3px]"/>
                </div>
                <h2 className="text-2xl md:text-3xl font-black text-foreground mb-2 md:mb-4 handwritten uppercase">JENI
                  GATI?</h2>
                <p className="font-bold handwritten text-slate-500 mb-6 md:mb-8 text-sm md:text-base">Kur
                  të jeni gati për të sfiduar Gjeniun, kliko butonin!</p>

                <div className="flex flex-col-reverse sm:flex-row gap-2.5 md:gap-3">
                  <button
                      onClick={resetGame}
                      className="flex-1 bg-card text-slate-500 py-3.5 md:py-5 doodle-border shadow-doodle font-black text-lg md:text-xl hover:translate-x-[-2px] hover:translate-y-[-2px] transition-all active:scale-95 hover:bg-rose-50 hover:text-rose-500"
                  >
                    KTHEHU
                  </button>
                  <button
                      onClick={triggerReady}
                      className="flex-1 bg-emerald-500 text-white py-3.5 md:py-5 doodle-border shadow-doodle md:shadow-doodle-lg font-black text-lg md:text-xl hover:translate-x-[-2px] hover:translate-y-[-2px] transition-all active:scale-95"
                  >
                    FILLO!
                  </button>
                </div>
              </motion.div>
            </div>
        ) : status === 'COUNTDOWN' ? (
            <div data-nosnippet>
              <CountdownView/>
            </div>
        ) : status === 'RECONNECTING' ? (
            <div data-nosnippet className="h-[100dvh] bg-background flex flex-col items-center justify-center p-4 text-center">
              <motion.div
                  initial={{opacity: 0, scale: 0.9}}
                  animate={{opacity: 1, scale: 1}}
                  className="w-full max-w-md bg-card doodle-border p-6 md:p-12 shadow-doodle-xl"
              >
                <div
                    className="w-16 h-16 md:w-20 md:h-20 bg-sky-100 doodle-border-soft flex items-center justify-center mb-4 md:mb-6 mx-auto animate-pulse shadow-doodle-sm !shadow-[#0EA5E9]">
                  <Loader2 className="w-8 h-8 md:w-10 md:h-10 text-sky-500 animate-spin"/>
                </div>
                <h2 className="text-2xl md:text-3xl font-black text-foreground mb-2 md:mb-4 handwritten uppercase tracking-tight">PO
                  KTHEHEMI...</h2>
                <p className="font-bold handwritten text-slate-500 text-sm md:text-base">Po kthehemi
                  aty ku mbetëm. Edhe pak durim!</p>
              </motion.div>
            </div>
        ) : status === 'OPPONENT_LEFT' ? (
            <div data-nosnippet>
              <OpponentLeftView/>
            </div>
        ) : status === 'WAITING' ? (
            <div data-nosnippet>
              <WaitingScreen/>
            </div>
        ) : status === 'MATCHING' || status === 'MATCH_DECLINED' ? (
            <div data-nosnippet>
              <MatchingView/>
            </div>
        ) : (
            <div data-nosnippet>
              <GameBoard/>
            </div>
        )}

        {/* Global Lobby UI - Only on Lobby */}
        {status === 'LOBBY' && (
            <div data-nosnippet>
              <div className="fixed bottom-6 right-6 z-40 flex flex-col items-end gap-3">
                <FloatingSuggestButton 
                  onClick={() => openFeedback('SUGGESTION')} 
                />
                
                <RulesTriggerIcon
                    onClick={openRules}
                    hasSeenRules={hasSeenRules}
                />

                <div className="relative group">
                  {/* Tooltip on the LEFT side */}
                  <div className="absolute right-full mr-4 top-1/2 -translate-y-1/2 hidden md:flex flex-col items-end bg-card doodle-border-soft p-2.5 shadow-doodle-sm opacity-0 translate-x-4 group-hover:opacity-100 group-hover:translate-x-0 transition-all pointer-events-none whitespace-nowrap min-w-max border-2 border-sky-100 z-50">
                    <span className="text-[10px] font-black text-sky-600 uppercase tracking-widest handwritten leading-none mb-1">CILËSIMET</span>
                    <span className="text-[9px] font-bold text-slate-500 handwritten leading-none">Ndrysho opsionet e lojës</span>
                  </div>

                  <div
                      onClick={() => setIsSettingsOpen(true)}
                      className="p-2 md:p-3 bg-sky-400 doodle-border-soft cursor-pointer transition-transform hover:scale-110 active:scale-95 group/btn"
                      style={{transform: 'rotate(2.13605deg)'}}
                  >
                    <Settings
                        className="w-5 h-5 md:w-8 md:h-8 text-white group-hover/btn:rotate-90 transition-transform duration-500 stroke-[3px]"/>
                  </div>
                </div>
              </div>
              <RulesModal isOpen={isRulesOpen} onClose={closeRules}/>
            </div>
        )}

        {/* Global Disconnection Overlay */}
        <div data-nosnippet>
          <AnimatePresence>
            {isOpponentDisconnected && (
                <motion.div
                    initial={{opacity: 0}}
                    animate={{opacity: 1}}
                    exit={{opacity: 0}}
                    className="fixed inset-0 bg-card/80 backdrop-blur-[2px] flex flex-col items-center justify-center p-6 z-[60]"
                >
                  <div
                      className="bg-card doodle-border p-8 shadow-[10px_10px_0px_0px_var(--border)] flex flex-col items-center gap-6 max-w-sm w-full">
                    <div
                        className="w-20 h-20 bg-amber-100 doodle-border-soft flex items-center justify-center animate-pulse shadow-[4px_4px_0px_0px_#F59E0B]">
                      <WifiOff className="w-10 h-10 text-amber-500"/>
                    </div>
                    <div className="space-y-2 text-center">
                      <h2 className="text-2xl font-black text-foreground handwritten uppercase tracking-tight">LIDHJA
                        U SHKËPUT!</h2>
                      <p className="font-bold handwritten text-slate-500">Duke pritur kundërshtarin të
                        rikthehet...</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {[0, 1, 2].map((i) => (
                          <motion.div
                              key={i}
                              animate={{scale: [1, 1.5, 1], opacity: [0.3, 1, 0.3]}}
                              transition={{duration: 1, repeat: Infinity, delay: i * 0.2}}
                              className="w-2 h-2 bg-amber-400 rounded-full"
                          />
                      ))}
                    </div>
                  </div>
                </motion.div>
            )}
          </AnimatePresence>

          <GameRatingModal 
            isOpen={isRatingModalVisible} 
            onClose={() => {
              setIsRatingModalVisible(false);
              setHasDismissedRating(true);
            }} 
            autoPopped={isRatingAutoPopped}
          />

          <FeedbackModal
            isOpen={isFeedbackModalOpen}
            onClose={() => setIsFeedbackModalOpen(false)}
            type={feedbackCategory}
          />

          <GameDemoOverlay 
            isOpen={isDemoOpen} 
            onClose={() => setIsDemoOpen(false)} 
          />
        </div>
      </>
  );
}

function MatchingView() {
  const {gameState, triggerReady, refuseMatch, refusedByPlayerId, status, playerId} = useGame();

  if (!gameState) return null;

  const myPlayer = gameState.player1.playerId === playerId ? gameState.player1 : gameState.player2;
  const opponent = gameState.player1.playerId === playerId ? gameState.player2 : gameState.player1;

  const isMeRefused = refusedByPlayerId === myPlayer?.playerId;
  const isOpponentRefused = refusedByPlayerId === opponent?.playerId;

  const isDeclined = status === 'MATCH_DECLINED';

  const isMeReady = !isDeclined && myPlayer?.readiness === 'READY' && !isMeRefused;
  const isOpponentReady = !isDeclined && opponent?.readiness === 'READY' && !isOpponentRefused;

  const isPrivate = gameState.mode === 'PVP_PRIVATE_ROOM';

  const particles = React.useMemo(() => [...Array(6)].map((_, i) => ({
    id: i,
    duration: 3 + Math.random() * 2,
    delay: Math.random() * 2,
    top: Math.random() * 100,
    left: Math.random() * 100
  })), []);

  return (
      <div
          className="h-[100dvh] bg-background flex flex-col items-center justify-center p-4 text-center overflow-hidden relative">
        {/* Decorative background doodles */}
        <div className="absolute inset-0 pointer-events-none opacity-10">
          <motion.div
              animate={{
                scale: [1, 1.1, 1],
                rotate: [0, 10, -10, 0]
              }}
              transition={{duration: 10, repeat: Infinity}}
              className="absolute top-1/4 left-10 text-pink-400"
          >
            <Sparkles size={120}/>
          </motion.div>
          <motion.div
              animate={{
                scale: [1.1, 1, 1.1],
                rotate: [0, -10, 10, 0]
              }}
              transition={{duration: 8, repeat: Infinity}}
              className="absolute bottom-1/4 right-10 text-sky-400"
          >
            <Swords size={100}/>
          </motion.div>
        </div>

        <motion.div
            initial={{opacity: 0, scale: 0.8}}
            animate={{opacity: 1, scale: 1}}
            className="w-full max-w-2xl relative z-10"
        >
          <div className="space-y-2 md:space-y-12">
            {/* Main Title with organic movement */}
            <motion.div
                animate={{y: [0, -5, 0]}}
                transition={{duration: 2, repeat: Infinity, ease: "easeInOut"}}
                className="space-y-1 md:space-y-4"
            >
              <div
                  className="inline-block bg-amber-400 text-white px-3 py-1 md:px-6 md:py-2 doodle-border rotate-[-2deg] font-black text-sm md:text-xl handwritten shadow-doodle-sm">
                {isDeclined ? 'LOJA U ANULUA!' : isPrivate ? 'ERDHI SHOKU!' : 'U GJET NJË RIVAL!'}
              </div>
              <h2 className="text-xl md:text-7xl font-black text-foreground handwritten uppercase drop-shadow-sm leading-tight">
                {isDeclined ? (isPrivate ? 'PO KTHEHEMI NË LOB...' : 'PO TENTOJMË PËRSËRI...') : isPrivate ? <>SFIDË <span
                    className="text-sky-500">MIQËSORE</span>!</> : <>GATI PËR <span
                    className="text-pink-500">LOJË</span>? </>}
              </h2>
            </motion.div>

            <div
                className="relative flex flex-col md:flex-row items-center justify-center gap-1 md:gap-0 mt-1 md:mt-8">
              {/* My Card */}
              <motion.div
                  initial={{x: -100, opacity: 0, rotate: -15}}
                  animate={{x: 0, opacity: 1, rotate: -5}}
                  transition={{type: "spring", damping: 12, stiffness: 100, delay: 0.2}}
                  className="relative group z-20 scale-85 md:scale-100"
              >
                <div className={cn(
                    "bg-card doodle-border p-5 md:p-8 shadow-doodle md:shadow-doodle-lg flex flex-col items-center gap-2.5 md:gap-4 min-w-[160px] md:min-w-[200px] transition-all relative overflow-hidden",
                    isMeReady && "shadow-doodle-lg border-[#10B981] !shadow-[#10B981]",
                    isMeRefused && "shadow-doodle-lg border-rose-500 grayscale !shadow-[#F43F5E]"
                )}>
                  {(isMeReady || isMeRefused) && (
                      <motion.div
                          initial={{scale: 3, opacity: 0, rotate: -20}}
                          animate={{scale: 1, opacity: 1, rotate: -12}}
                          className="absolute inset-0 flex items-center justify-center pointer-events-none z-30"
                      >
                        <div className={cn(
                            "text-white px-4 py-1.5 md:px-6 md:py-2 doodle-border font-black text-lg md:text-2xl shadow-xl transform uppercase tracking-tighter",
                            isMeReady ? "bg-emerald-500" : "bg-rose-500"
                        )}>
                          {isMeReady ? 'GATI!' : 'REFUZOI!'}
                        </div>
                      </motion.div>
                  )}
                  <div className={cn(
                      "w-14 h-14 md:w-20 md:h-20 bg-rose-100 doodle-border-soft flex items-center justify-center transition-colors",
                      isMeReady && "bg-emerald-50 opacity-40",
                      isMeRefused && "bg-rose-50 opacity-40"
                  )}>
                    <User
                        className={cn("w-8 h-8 md:w-12 md:h-12 text-rose-500 stroke-[3px]", isMeReady && "text-emerald-500")}/>
                  </div>
                  <div
                      className={cn("space-y-0.5 md:space-y-1 transition-opacity", (isMeReady || isMeRefused) && "opacity-40")}>
                  <div
                      className="text-[9px] md:text-[12px] font-black text-rose-500 uppercase tracking-widest handwritten text-center">TI
                  </div>
                  <div
                      className="text-xl md:text-3xl font-black text-foreground handwritten">{myPlayer?.name}</div>
                  </div>                </div>
              </motion.div>

              {/* VS Starburst */}
              <motion.div
                  initial={{scale: 0, rotate: 180}}
                  animate={{scale: 1, rotate: 0}}
                  transition={{type: "spring", damping: 10, stiffness: 200, delay: 0.6}}
                  className="relative z-30 -my-6 md:-mx-8 scale-75 md:scale-100"
              >
                <div className="relative">
                  {/* Impact Glow */}
                  <div
                      className="absolute inset-0 bg-amber-400 rounded-full blur-2xl opacity-40 animate-pulse"/>

                  {/* The "VS" Bubble */}
                  <div
                      className="bg-amber-400 text-foreground w-14 h-14 md:w-20 md:h-20 rounded-full doodle-border flex items-center justify-center font-black italic text-2xl md:text-4xl shadow-doodle relative">
                    {isPrivate ? '&' : 'VS'}
                    <motion.div
                        animate={{rotate: 360}}
                        transition={{duration: 4, repeat: Infinity, ease: "linear"}}
                        className="absolute inset-[-6px] md:inset-[-8px] border-4 border-dashed border-amber-500/30 rounded-full"
                    />
                    <motion.div
                        animate={{scale: [1, 1.15, 1], opacity: [0.5, 0.2, 0.5]}}
                        transition={{duration: 1.5, repeat: Infinity}}
                        className="absolute inset-0 border-4 border-[var(--border)] rounded-full"
                    />
                  </div>
                </div>
              </motion.div>

              {/* Opponent Card */}
              <motion.div
                  initial={{x: 100, opacity: 0, rotate: 15}}
                  animate={{x: 0, opacity: 1, rotate: 5}}
                  transition={{type: "spring", damping: 12, stiffness: 100, delay: 0.4}}
                  className="relative group z-20 scale-85 md:scale-100"
              >
                <div className={cn(
                    "bg-card doodle-border p-5 md:p-8 shadow-doodle md:shadow-doodle-lg flex flex-col items-center gap-2.5 md:gap-4 min-w-[160px] md:min-w-[200px] transition-all relative overflow-hidden",
                    isOpponentReady && "shadow-doodle-lg border-[#10B981] !shadow-[#10B981]",
                    isOpponentRefused && "shadow-doodle-lg border-rose-500 grayscale !shadow-[#F43F5E]"
                )}>
                  {(isOpponentReady || isOpponentRefused) && (
                      <motion.div
                          initial={{scale: 3, opacity: 0, rotate: -20}}
                          animate={{scale: 1, opacity: 1, rotate: -12}}
                          className="absolute inset-0 flex items-center justify-center pointer-events-none z-30"
                      >
                        <div className={cn(
                            "text-white px-4 py-1.5 md:px-6 md:py-2 doodle-border font-black text-lg md:text-2xl shadow-xl transform uppercase tracking-tighter",
                            isOpponentReady ? "bg-emerald-500" : "bg-rose-500"
                        )}>
                          {isOpponentReady ? 'GATI!' : 'REFUZOI!'}
                        </div>
                      </motion.div>
                  )}
                  <div className={cn(
                      "w-14 h-14 md:w-20 md:h-20 bg-sky-100 doodle-border-soft flex items-center justify-center transition-colors",
                      isOpponentReady && "bg-emerald-50 opacity-40",
                      isOpponentRefused && "bg-rose-50 opacity-40"
                  )}>
                    <User
                        className={cn("w-8 h-8 md:w-12 md:h-12 text-sky-500 stroke-[3px]", isOpponentReady && "text-emerald-500")}/>
                  </div>
                  <div
                      className={cn("space-y-0.5 md:space-y-1 transition-opacity", (isOpponentReady || isOpponentRefused) && "opacity-40")}>
                    <div
                        className="text-[9px] md:text-[12px] font-black text-sky-400 uppercase tracking-widest handwritten text-center">KUNDËRSHTARI
                    </div>
                    <div
                        className="text-xl md:text-3xl font-black text-foreground handwritten truncate w-full px-2">{opponent?.name}</div>
                  </div>
                </div>
              </motion.div>
            </div>

            {!isDeclined && (
                <motion.div
                    initial={{y: 50, opacity: 0}}
                    animate={{y: 0, opacity: 1}}
                    transition={{delay: 0.8}}
                    className="flex flex-col-reverse sm:flex-row items-center justify-center gap-2 md:gap-6 pt-2 md:pt-12"
                >
                  <button
                      onClick={refuseMatch}
                      disabled={isMeReady}
                      className={cn(
                          "w-full sm:w-auto px-6 py-2.5 md:px-12 md:py-5 bg-card text-slate-400 doodle-border shadow-doodle font-black text-base md:text-2xl hover:translate-x-[-2px] hover:translate-y-[-2px] transition-all active:scale-95 hover:bg-rose-50 hover:text-rose-500 group",
                          isMeReady && "opacity-50 cursor-not-allowed"
                      )}
                  >
                <span className="flex items-center justify-center gap-2">
                  REFUZO
                </span>
                  </button>
                  <button
                      onClick={triggerReady}
                      disabled={isMeReady}
                      className={cn(
                          "w-full sm:w-auto px-8 py-3.5 md:px-16 md:py-6 bg-sky-500 text-white doodle-border shadow-doodle md:shadow-doodle-lg font-black text-lg md:text-3xl hover:translate-x-[-4px] hover:translate-y-[-4px] transition-all active:scale-95",
                          !isMeReady && "animate-bounce-subtle",
                          isMeReady && "bg-emerald-500 shadow-doodle opacity-80 cursor-default"
                      )}
                  >
                <span className="flex items-center justify-center gap-2 md:gap-4">
                  {isMeReady ? (
                      <>
                        <Loader2 className="w-5 h-5 md:w-10 md:h-10 animate-spin"/>
                        <span>NË PRITJE...</span>
                      </>
                  ) : (
                      <>
                        <Play className="w-5 h-5 md:w-10 md:h-10 fill-current"/>
                        <span>PRANO!</span>
                      </>
                  )}
                </span>
                  </button>
                </motion.div>
            )}
          </div>
        </motion.div>

        {/* Background Star Particles */}
        {particles.map((p) => (
            <motion.div
                key={p.id}
                initial={{opacity: 0}}
                animate={{
                  opacity: [0, 0.5, 0],
                  scale: [0.5, 1, 0.5],
                  y: [-20, 20]
                }}
                transition={{
                  duration: p.duration,
                  repeat: Infinity,
                  delay: p.delay
                }}
                className="absolute text-amber-300 pointer-events-none"
                style={{
                  top: `${p.top}%`,
                  left: `${p.left}%`
                }}
            >
              <Gamepad2 size={24}/>
            </motion.div>
        ))}
      </div>
  );
}

function CountdownView() {
  const {setStatus} = useGame();
  const [count, setCount] = React.useState(3);

  const particles = React.useMemo(() => [...Array(10)].map((_, i) => ({
    id: i,
    duration: 2 + Math.random() * 2,
    delay: Math.random() * 2,
    top: Math.random() * 100,
    left: Math.random() * 100
  })), []);

  React.useEffect(() => {
    if (count > 0) {
      const timer = setTimeout(() => setCount(count - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      // Give more time to see the "PO FILLON" message
      const timer = setTimeout(() => setStatus('PLAYING'), 2000);
      return () => clearTimeout(timer);
    }
  }, [count, setStatus]);

  return (
      <div
          className="h-[100dvh] bg-background flex flex-col items-center justify-center p-4 text-center overflow-hidden relative">
        {/* Dynamic Background Accents */}
        <AnimatePresence>
          {count > 0 && (
              <motion.div
                  initial={{opacity: 0, scale: 0.8}}
                  animate={{opacity: 0.15, scale: 1.2}}
                  exit={{opacity: 0, scale: 1.5}}
                  className="absolute inset-0 flex items-center justify-center pointer-events-none"
              >
                <div className={cn(
                    "w-[300px] h-[300px] md:w-[500px] md:h-[500px] rounded-full blur-3xl transition-colors duration-500",
                    count === 3 ? "bg-pink-400" : count === 2 ? "bg-sky-400" : "bg-amber-400"
                )}/>
              </motion.div>
          )}
        </AnimatePresence>

        <div className="relative z-10 flex items-center justify-center w-full h-full">
          <AnimatePresence>
            {count > 0 ? (
                <motion.div
                    key={`count-${count}`}
                    initial={{scale: 0, rotate: -30, opacity: 0}}
                    animate={{
                      scale: 1,
                      rotate: 0,
                      opacity: 1
                    }}
                    exit={{scale: 1.5, opacity: 0, rotate: 20, filter: "blur(10px)"}}
                    transition={{type: "spring", damping: 15, stiffness: 300}}
                    className="absolute"
                >
                  <div className={cn(
                      "text-[10rem] md:text-[20rem] font-black handwritten leading-none drop-shadow-doodle md:drop-shadow-[15px_15px_0px_var(--border)]",
                      count === 3 ? "text-pink-500" : count === 2 ? "text-sky-500" : "text-amber-500"
                  )}>
                    {count}
                  </div>
                </motion.div>
            ) : (
                <motion.div
                    key="start-msg"
                    initial={{scale: 0.8, opacity: 0, rotate: -5}}
                    animate={{scale: 1, opacity: 1, rotate: 0}}
                    transition={{type: "spring", damping: 12, stiffness: 200}}
                    className="relative px-4"
                >
                  {/* The "Notebook Page" Card */}
                  <div
                      className="bg-card doodle-border p-8 md:p-20 shadow-doodle-xl relative overflow-hidden">
                    {/* Decorative Notebook Lines */}
                    <div
                        className="absolute inset-0 opacity-10 pointer-events-none flex flex-col justify-between p-4 gap-4">
                      {[...Array(10)].map((_, i) => (
                          <div key={i} className="h-[1px] w-full bg-sky-400"/>
                      ))}
                    </div>

                    <div className="relative z-10 space-y-1 md:space-y-2 text-center">
                      <motion.div
                          initial={{y: -50, opacity: 0}}
                          animate={{y: 0, opacity: 1}}
                          transition={{delay: 0.2, type: "spring", bounce: 0.6}}
                      >
                        <h1 className="text-5xl md:text-[10rem] font-black text-foreground handwritten uppercase tracking-tighter leading-none select-none">
                          LOJA
                        </h1>
                      </motion.div>

                      <motion.div
                          initial={{scale: 0}}
                          animate={{scale: 1}}
                          transition={{delay: 0.5, type: "spring"}}
                          className="bg-amber-400 text-white px-5 py-1.5 md:px-10 md:py-4 doodle-border shadow-doodle inline-block"
                      >
                        <h2 className="text-2xl md:text-7xl font-black handwritten uppercase tracking-wide">
                          PO FILLON!
                        </h2>
                      </motion.div>
                    </div>

                    {/* Hand-drawn accents that pop in */}
                    <motion.div
                        initial={{scale: 0}}
                        animate={{scale: 1}}
                        transition={{delay: 0.8, type: "spring"}}
                        className="absolute top-2 md:top-4 right-2 md:right-4 text-pink-500 rotate-12"
                    >
                      <Sparkles className="w-8 h-8 md:w-12 md:h-12"/>
                    </motion.div>
                    <motion.div
                        initial={{scale: 0}}
                        animate={{scale: 1}}
                        transition={{delay: 1, type: "spring"}}
                        className="absolute bottom-2 md:bottom-4 left-2 md:left-4 text-sky-500 -rotate-12"
                    >
                      <Dices className="w-8 h-8 md:w-12 md:h-12"/>
                    </motion.div>
                  </div>

                  {/* "Sticker" Badges below the card */}
                  <div
                      className="flex items-center justify-center gap-4 -mt-4 md:-mt-6 relative z-20">
                    <motion.div
                        initial={{y: 20, opacity: 0, rotate: -15}}
                        animate={{y: 0, opacity: 1, rotate: -8}}
                        transition={{delay: 1.2, type: "spring"}}
                        className="px-4 py-1.5 md:px-6 md:py-2 bg-sky-100 text-sky-600 doodle-border-soft font-black handwritten text-lg md:text-2xl shadow-doodle-sm"
                    >
                      SUKSESE!
                    </motion.div>
                  </div>
                </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Background Sparkles */}
        {particles.map((p) => (
            <motion.div
                key={p.id}
                animate={{
                  opacity: [0, 0.4, 0],
                  scale: [0.5, 1, 0.5],
                  y: [-100, 100]
                }}
                transition={{
                  duration: p.duration,
                  repeat: Infinity,
                  delay: p.delay
                }}
                className="absolute text-amber-300 pointer-events-none"
                style={{
                  top: `${p.top}%`,
                  left: `${p.left}%`
                }}
            >
              <Gamepad2 size={20}/>
            </motion.div>
        ))}
      </div>
  );
}

function DictionaryInfoModal({ isOpen, onClose }: { isOpen: boolean, onClose: () => void }) {
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-md">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="relative w-full max-w-md bg-card doodle-border p-6 md:p-10 shadow-doodle-xl overflow-hidden text-center"
          >
            <button 
              onClick={onClose}
              className="absolute top-4 right-4 p-2 hover:bg-muted rounded-full transition-colors boing"
            >
              <X className="w-6 h-6 text-slate-400" />
            </button>

            <div className="space-y-6">
              <div className="w-16 h-16 bg-sky-100 doodle-border-soft flex items-center justify-center mx-auto -rotate-6 shadow-doodle-sm !shadow-sky-400">
                <Info className="w-8 h-8 text-sky-500" />
              </div>

              <div className="space-y-2">
                <h2 className="text-2xl md:text-3xl font-black text-foreground handwritten uppercase">RRETH FJALORIT</h2>
                <div className="h-1 w-20 bg-sky-400 mx-auto rounded-full" />
              </div>

              <p className="text-foreground font-bold handwritten text-sm md:text-base leading-relaxed text-left">
                Fjalori i <span className="text-pink-500">Fjala</span><span className="text-sky-500">Fundit</span> është ndërtuar duke u bazuar në korpuse gjuhësore dhe burime të hapura digjitale.
                Për shkak të natyrës automatike të mbledhjes së këtyre të dhënave, ekziston mundësia që disa fjalë të mos jenë pjesë e gjuhës standarde shqipe ose të përmbajnë gabime teknike. 
                <br /><br />
                Ju kërkojmë ndjesë për çdo pasaktësi dhe po punojmë vazhdimisht për pastrimin dhe pasurimin e mëtejshëm të fjalorit.
              </p>

              <button 
                onClick={onClose}
                className="w-full bg-sky-500 text-white py-4 doodle-border shadow-doodle-lg font-black text-xl hover:translate-x-[-2px] hover:translate-y-[-2px] transition-all active:scale-95"
              >
                E KUPTOVA
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
